'use client';

import { RemoveScroll } from 'react-remove-scroll';
import { ChangeEvent, DragEvent, useState } from 'react';
import cn from 'classnames';

import { PictureStepActions } from '../../state';
import { RepairFormState, StepMap } from '../../../../constants';
import { uploadImage } from './helpers/uploadImage';
import { RepairFormActionMap } from '../../../../reducer';

import { HeadingText } from '@/components/atoms/HeadingText';
import { ImageListItem } from '@/components/infinity/ImageListItem';
import { useFormContext } from '@/components/forms/FormContext';
import { IconArrowLeft, IconCamera, IconInfoBubble } from '@/components/icons';
import { Select } from '@/components/atoms/Inputs/Select';
import { TextAlert } from '@/components/calculators/TextAlert';
import { ModalFullScreen } from '@/components/ModalFullScreen';
import { TextLink } from '@/components/TextLink';
import DesktopUpload from './DesktopUpload';
import { FinishOnPhone } from '../FinishOnPhone';

export interface UploadPhotoProps {
	testId?: string;
}

const UploadPhoto = (props: UploadPhotoProps) => {
	const { testId = 'UploadPhoto' } = props;
	const { dispatch, state } = useFormContext<
		StepMap,
		RepairFormState,
		RepairFormActionMap
	>();

	const [dragZoneActive, setDragZoneActive] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [showUpload, setShowUpload] = useState(true);
	const [showQR, setShowQR] = useState(false);

	function handleDropImage(event: DragEvent<HTMLButtonElement>) {
		event.preventDefault();
		const droppedPhotos = event.dataTransfer.files;
		if (droppedPhotos.length > 0) {
			const files: File[] = Array.from(droppedPhotos);
			uploadImage(state, dispatch, files);
		}
	}

	function handleAddImage(event: ChangeEvent<HTMLInputElement>) {
		const files: File[] = Array.from(event.target.files || []);
		uploadImage(state, dispatch, files);
		if (showModal) {
			setShowModal(false);
		}
		setDragZoneActive(false);
	}

	function handleDelete(id: string) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore this is just a poc and this'll work
		dispatch({ type: PictureStepActions.REMOVE_PICTURE, payload: { id } });
	}

	function handleLocationChange(event: React.ChangeEvent<HTMLSelectElement>) {
		dispatch({
			type: PictureStepActions.UPDATE_SYSTEM_LOCATION,
			payload: event.target.value,
		});
	}

	// Drag and drop events
	function handleDragOver(event: DragEvent<HTMLButtonElement>) {
		event.preventDefault();
		event.stopPropagation();
		setDragZoneActive(true);
	}

	function handleDragEnter(event: DragEvent<HTMLButtonElement>) {
		event.preventDefault();
		event.stopPropagation();
		setDragZoneActive(true);
	}

	function handleDragLeave(event: DragEvent<HTMLButtonElement>) {
		event.preventDefault();
		event.stopPropagation();
		setDragZoneActive(false);
	}

	return (
		<div data-testid={testId}>
			<HeadingText tag="h1" size="h2" className="mb-10 lg:mb-14" balanced>
				Okay, upload a photo of your outside unit label
			</HeadingText>
			<button
				className={cn(
					'mx-auto mb-4 block w-full max-w-85 rounded-xl border border-dashed border-slate-300 transition-all hover:cursor-pointer hover:bg-slate-100 lg:max-w-101',
					{ 'bg-slate-100': dragZoneActive },
					{ 'bg-transparent': !dragZoneActive },
				)}
				onClick={() => setShowModal(true)}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDrop={handleDropImage}
				onDragLeave={handleDragLeave}
			>
				<div className="flex w-full items-center justify-center p-3">
					<IconCamera className="h-8 w-8 fill-slate-500" />{' '}
					<span className="ml-2 text-slate-500">
						{state.pictures.length > 0
							? 'Upload another photo'
							: 'Upload a photo'}
					</span>
				</div>
				<input
					type="file"
					name="files"
					id="quote"
					accept="application/pdf, image/png, image/jpeg, image/webp, image/heic, environment"
					multiple
					onChange={handleAddImage}
					className="hidden"
				/>
			</button>

			<div className="mx-auto mb-8 grid max-w-85 gap-4 lg:mb-10 lg:max-w-101">
				{state.pictures
					.filter((image) => image !== null)
					.map((image) => (
						<ImageListItem
							key={image.id}
							id={image.id}
							name={image.name}
							removeInput={handleDelete}
							inProgress={image.inProgress}
							error={image.error}
						/>
					))}

				<Select
					id="systemLocation"
					name="systemLocation"
					label="Furnace or air handler location"
					value={state.systemLocation}
					onChange={handleLocationChange}
				>
					<option></option>
					<option value="Basement">Basement</option>
					<option value="Crawlspace">Crawlspace</option>
					<option value="Attic">Attic</option>
					<option value="Closet">Closet</option>
					<option value="Multiple Locations">Multiple locations</option>
					<option value="Other">Other</option>
					<option value="Not Sure">Not Sure</option>
					<option value="I don't have one">I don&apos;t have one</option>
				</Select>
			</div>

			<TextAlert
				id="WhyAskingPhoto"
				renderIcon={IconInfoBubble}
				title="Why are we asking?"
				text="The label of your outdoor unit and heating source location tell us the system size and efficiency details we need to maximize quote accuracy and home comfort."
			/>

			{showModal && (
				<RemoveScroll>
					<ModalFullScreen
						closeButton={
							<TextLink
								iconRender={() => (
									<IconArrowLeft className="mr-2 inline-block h-7 w-7 fill-slate-400 text-slate-400 group-hover:fill-slate-700 md:h-9 md:w-9" />
								)}
								iconPosition="left"
								onClick={() => setShowModal(false)}
							>
								<span className="sr-only"></span>
							</TextLink>
						}
					>
						<div className="flex max-w-5xl flex-col items-center justify-center gap-4 text-center">
							{showUpload && (
								<DesktopUpload
									handleAddImage={handleAddImage}
									setShowQR={setShowQR}
									setShowUpload={setShowUpload}
								/>
							)}
							{showQR && (
								<FinishOnPhone
									setShowQR={setShowQR}
									setShowUpload={setShowUpload}
								/>
							)}
						</div>
					</ModalFullScreen>
				</RemoveScroll>
			)}
		</div>
	);
};

export default UploadPhoto;
